
import IconEdit from "@/components/common/icon/IconEdit.vue";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import Pagination from "@/components/common/Pagination.vue";
import { IServicepoint, IServicepointData, IServicepointParams } from "@/entities";
import {
  IInformationOptionsServicePoint,
  IServicePointReportRequest,
} from "../../services/backend";
import { EDirection, IPageResponse } from "@/services";
import VueFilters from "@/vue-filters";
import { BvTableFieldArray } from "bootstrap-vue";
import { Component, Vue } from "vue-property-decorator";
import { Action } from "vuex-class";
import ServicepointsFilter from "@/components/servicepoint/ServicepointsFilter.vue";
import { EventBus } from "@/event-bus";
import { Dictionary } from "vue-router/types/router";
import { SERVICEPOINT_TYPE } from "@/constans";
import { i18n } from "@/i18n";
import { SweetAlertResult } from "sweetalert2";

@Component({
  name: "servicepoints-table",
  mixins: [VueFilters],
  components: {
    ServicepointsFilter,
    IconTrash,
    IconEdit,
    Pagination,
  },
})
export default class ServicepointsTable extends Vue {
  @Action("fetchServicepointsPage") fetchServicepointsPage!: (
    params: IServicepointParams
  ) => Promise<IPageResponse<IServicepointData> | undefined>;
  @Action("updateStatusServicepoint") updateStatusServicepoint!: (id: number) => Promise<void>;
  @Action("generateServicePointReport") fetchGenerateServicePointReport!: (
    params: IServicePointReportRequest
  ) => Promise<void>;

  private servicepoints: IPageResponse<IServicepoint> | undefined = {
    content: [] as IServicepoint[],
  } as IPageResponse<IServicepoint>;

  private queryParams = this.$route.query;
  private params: IServicepointParams = {
    active: this.$ability.can("read", "ServicepointEditActive")
      ? this.queryParams.active !== "false"
      : true,
    address: this.queryParams.address?.toString(),
    clientDesc: this.queryParams.clientDesc?.toString(),
    codeSol: this.queryParams.codeSol?.toString(),
    countryId: this.queryParams.countryId ? Number(this.queryParams.countryId) : undefined,
    description: this.queryParams.description?.toString(),
    deviceId: this.queryParams.deviceId ? Number(this.queryParams.deviceId) : undefined,
    idSmartcash: this.queryParams.idSmartcash?.toString(),
    clientPrecise: this.queryParams.clientPrecise?.toString() === "true" || false,
    codePrecise: this.queryParams.codePrecise?.toString() === "true" || false,
    descriptionPrecise: this.queryParams.descriptionPrecise?.toString() === "true" || false,
    deviceIdPrecise: this.queryParams.deviceIdPrecise?.toString() === "true" || false,
    idCashPrecise: this.queryParams.idCashPrecise?.toString() === "true" || false,
    addressPrecise: this.queryParams.addressPrecise?.toString() === "true" || false,
    type:
      this.queryParams.type in SERVICEPOINT_TYPE
        ? (this.queryParams.type as SERVICEPOINT_TYPE)
        : undefined,
    flowApproval:
      this.queryParams.flowApproval === "true"
        ? true
        : this.queryParams.flowApproval === "false"
        ? false
        : undefined,
  };

  private pageNumber = Number(this.queryParams.page) || 1;
  private pageSize = 10;
  private totalElements = 0;
  private orderBy = this.queryParams.orderBy?.toString() || "id";
  private sortDesc = this.queryParams.direction !== "ASC";

  constructor() {
    super();
  }

  mounted(): void {
    this.getServicepoints();
    EventBus.$on("update-servicepoints-table", () => {
      this.onChangeCurrentPage(1);
    });
  }

  async beforeDestroy(): Promise<void> {
    EventBus.$off("update-servicepoints-table");
  }

  async onChangeCurrentPage(currentPage: number): Promise<void> {
    this.pageNumber = currentPage;
    await this.getServicepoints();
  }

  async filterServicepints(): Promise<void> {
    this.$nextTick(async () => this.getServicepoints());
  }

  async getServicepoints(): Promise<void> {
    this.params.size = this.pageSize;
    this.params.orderBy = this.orderBy || undefined;
    this.params.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
    this.params.page = this.pageNumber;
    await this.$router
      .replace({ path: this.$route.path, query: this.params as Dictionary<string> })
      .catch(() => {
        // do nothing.
      });
    this.params.page = this.pageNumber - 1;
    this.servicepoints = await this.fetchServicepointsPage(this.params);

    this.totalElements = this.servicepoints?.totalElements || 0;
  }

  get showInactive(): boolean {
    return !this.params.active;
  }

  set showInactive(value: boolean) {
    this.params.active = !value;
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first",
      },
      {
        key: "id",
        label: this.$t("column.id") as string,
        sortable: true,
        thClass: "d-none",
        tdClass: "d-none",
      },
      {
        key: "type",
        label: this.$t("column.type") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "subclient.client.codeSol,subclient.client.description",
        label: this.$t("column.client") as string,
        formatter: (_value: string, _key: string, item: IServicepoint): string => {
          return `${item.subclient?.client?.codeSol} - ${item.subclient?.client?.description}`;
        },
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "subclient.codeSol,subclient.description",
        label: this.$t("column.subclient") as string,
        formatter: (_value: string, _key: string, item: IServicepoint): string => {
          return `${item.subclient?.codeSol} - ${item.subclient?.description}`;
        },
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "codeSol",
        label: this.$t("column.code") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "description",
        label: this.$t("column.description") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "deviceId",
        label: this.$t("label.device.id") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "active",
        label: this.$t("column.active") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thAttr: this.$ability.can("read", "ServicepointEditActive") ? null : { hidden: true },
        tdAttr: this.$ability.can("read", "ServicepointEditActive") ? null : { hidden: true },
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last",
      },
    ];
  }

  get serviceServicePointReportParams(): IServicePointReportRequest {
    return {
      cashServicepointParamsDTO: this.params,
      informationOptionsServicePoint: this.informationOptionsServicePoint,
    } as IServicePointReportRequest;
  }

  async updateStatusServicepointLocal(servicepoint: IServicepoint): Promise<void> {
    servicepoint.active = !servicepoint.active;
    await this.updateStatusServicepoint(servicepoint.id!);
    await this.getServicepoints();
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("label.confirmation.to.export.message").toString(),
      text: this.$t("label.confirmation.to.export.message.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  private async executeReportServicePoint(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      await this.fetchGenerateServicePointReport(this.serviceServicePointReportParams);
      this.makeToast("success", this.$t("message.usersreport.ok").toString());
    }
  }

  private get informationOptionsServicePoint(): IInformationOptionsServicePoint {
    return {
      includeCountryId: true,
      includeActive: true,
      includeEntityCode: true,
      includeEntityDesc: true,
      includeSubClientCode: true,
      includeSubClientDesc: true,
      includeId: true,
      includeDescription: true,
      includeType: true,
      includeDeviceId: true,
      includeModules: true,
    } as IInformationOptionsServicePoint;
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
